import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FileText,
  Shield,
  Scale,
  AlertTriangle,
  Link2,
  Globe,
  Gavel,
  Mail,
  User,
  Lock
} from 'lucide-react';

const sections = [
  { id: 'introduction', title: 'Introduction', icon: FileText },
  { id: 'acceptance', title: 'Acceptance of Terms', icon: Scale },
  { id: 'changes', title: 'Changes to Terms', icon: Globe },
  { id: 'use-of-service', title: 'Use of Service', icon: User },
  { id: 'intellectual-property', title: 'Intellectual Property', icon: Shield },
  { id: 'prohibited-uses', title: 'Prohibited Uses', icon: AlertTriangle },
  { id: 'third-party', title: 'Third-Party Links & Services', icon: Link2 },
  { id: 'disclaimer', title: 'Disclaimers', icon: FileText },
  { id: 'liability', title: 'Limitation of Liability', icon: Scale },
  { id: 'indemnification', title: 'Indemnification', icon: Gavel },
  { id: 'privacy', title: 'Privacy & Data', icon: Lock },
  { id: 'termination', title: 'Termination', icon: Gavel },
  { id: 'governing-law', title: 'Governing Law', icon: Globe },
  { id: 'contact', title: 'Contact Us', icon: Mail }
];

const lastUpdated = new Date().toLocaleDateString(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Tasks IT Services</title>
        <meta
          name="description"
          content="Review the Terms of Service for Tasks IT Services, including usage guidelines, intellectual property, liability, and privacy."
        />
      </Helmet>

      {/* Background */}
      <div className="relative min-h-screen overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900" />
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[30rem] h-[30rem] bg-blue-600/20 rounded-full blur-3xl" />

        {/* Hero */}
        <section className="relative pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl shadow-xl mb-6">
                <FileText className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-5xl font-bold mb-3">Terms of Service</h1>
              <p className="text-blue-100 max-w-2xl mx-auto">
                Please read these Terms carefully. By accessing or using our services, you agree to be bound by these Terms.
              </p>
              <div className="mt-4 text-sm text-blue-200">
                Last updated: {lastUpdated}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="relative pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
            {/* TOC */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="lg:sticky lg:top-24"
              >
                <div className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6 text-white shadow-2xl">
                  <h3 className="text-lg font-semibold mb-4">On this page</h3>
                  <nav className="space-y-2">
                    {sections.map(({ id, title }) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="block px-3 py-2 rounded-lg text-sm text-blue-100 hover:text-white hover:bg-white/10 transition"
                      >
                        {title}
                      </a>
                    ))}
                  </nav>
                </div>
              </motion.div>
            </aside>

            {/* Main */}
            <div className="lg:col-span-8 xl:col-span-9 space-y-6">
              {sections.map(({ id, title, icon: Icon }, idx) => (
                <motion.section
                  key={id}
                  id={id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.45, delay: 0.02 * idx }}
                  className="relative group"
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/20 via-cyan-500/10 to-purple-500/20 blur-xl opacity-50 group-hover:opacity-80 transition" />
                  <div className="relative backdrop-blur-xl bg-white/10 border border-white/15 rounded-3xl p-6 md:p-8 text-white shadow-2xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold">{title}</h2>
                    </div>

                    {id === 'introduction' && (
                      <p className="text-blue-100 leading-relaxed">
                        These Terms of Service govern your use of the websites, products, and services provided by Tasks IT Services
                        (“Company”, “we”, “us”, or “our”). By accessing or using our services, you agree to these Terms and our{' '}
                        <Link to="/privacy" className="text-cyan-300 underline hover:text-cyan-200">Privacy Policy</Link>.
                      </p>
                    )}

                    {id === 'acceptance' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>You must be legally capable of entering into a binding agreement.</li>
                        <li>Use of the services constitutes acceptance of these Terms.</li>
                        <li>If you do not agree, do not use the services.</li>
                      </ul>
                    )}

                    {id === 'changes' && (
                      <p className="text-blue-100 leading-relaxed">
                        We may update these Terms from time to time. Changes are effective when posted on this page.
                        Continued use after changes constitutes acceptance. We will update the “Last updated” date accordingly.
                      </p>
                    )}

                    {id === 'use-of-service' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>Provide accurate information when submitting service enquiries or project requests.</li>
                        <li>Comply with applicable laws and regulations.</li>
                        <li>Do not attempt to disrupt, overload, or reverse engineer our services.</li>
                      </ul>
                    )}

                    {id === 'intellectual-property' && (
                      <p className="text-blue-100 leading-relaxed">
                        All content, branding, logos, software, and materials are owned by the Company or its licensors and are protected by
                        applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without prior consent.
                      </p>
                    )}

                    {id === 'prohibited-uses' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>Illegal, infringing, or fraudulent activities.</li>
                        <li>Uploading malicious code or attempting unauthorized access.</li>
                        <li>Harassment, hate speech, or abusive behavior.</li>
                        <li>Automated scraping without written permission.</li>
                      </ul>
                    )}

                    {id === 'third-party' && (
                      <p className="text-blue-100 leading-relaxed">
                        Our services may contain links to third-party sites or use third-party tools. We are not responsible for their content,
                        privacy practices, or availability. Use third-party services at your own risk and review their terms.
                      </p>
                    )}

                    {id === 'disclaimer' && (
                      <p className="text-blue-100 leading-relaxed">
                        Services are provided “as is” and “as available” without warranties of any kind, express or implied, including
                        merchantability, fitness for a particular purpose, and non-infringement. We do not warrant uninterrupted or error-free service.
                      </p>
                    )}

                    {id === 'liability' && (
                      <p className="text-blue-100 leading-relaxed">
                        To the maximum extent permitted by law, the Company shall not be liable for any indirect, incidental, special, consequential,
                        or exemplary damages arising from your use of the services, even if advised of the possibility of such damages.
                      </p>
                    )}

                    {id === 'indemnification' && (
                      <p className="text-blue-100 leading-relaxed">
                        You agree to indemnify and hold harmless the Company, its affiliates, and personnel from any claims, damages, liabilities,
                        and expenses arising from your use of the services or violation of these Terms.
                      </p>
                    )}

                    {id === 'privacy' && (
                      <p className="text-blue-100 leading-relaxed">
                        We handle personal data in accordance with our{' '}
                        <Link to="/privacy" className="text-cyan-300 underline hover:text-cyan-200">Privacy Policy</Link>.
                        By using our services, you consent to the collection and processing of your information as described therein.
                      </p>
                    )}

                    {id === 'termination' && (
                      <p className="text-blue-100 leading-relaxed">
                        We may suspend or terminate access to the services at any time, with or without cause or notice, including for violations of these Terms.
                        Upon termination, your right to use the services will cease immediately.
                      </p>
                    )}

                    {id === 'governing-law' && (
                      <p className="text-blue-100 leading-relaxed">
                        These Terms are governed by the laws applicable in our place of business, without regard to conflicts of law principles.
                        Any disputes shall be resolved in the competent courts of that jurisdiction.
                      </p>
                    )}

                    {id === 'contact' && (
                      <div className="text-blue-100 leading-relaxed space-y-2">
                        <p>Questions about these Terms? Contact us:</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <a href="mailto:GlennRosas@hotmail.com" className="underline text-cyan-300 hover:text-cyan-200">
                            GlennRosas@hotmail.com
                          </a>
                          <span className="opacity-60 hidden sm:inline">•</span>
                          <a href="tel:+81702633830" className="underline text-cyan-300 hover:text-cyan-200">
                            +81 70-2633-2830
                          </a>
                        </div>
                        <p className="text-sm text-blue-200/80">We aim to respond within 2–3 business days.</p>
                      </div>
                    )}
                  </div>
                </motion.section>
              ))}

              {/* Footer note */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center text-blue-200 text-sm mt-10"
              >
                © {new Date().getFullYear()} Tasks IT Services. All rights reserved.
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Terms;